// @section 1 Campaigns / Funnel — left-to-right pipeline over a DATE RANGE
// Reads analytics.v_campaign_master_daily_activity via /api/hq/campaigns/
// masters/{key}/daily — TRUE per-day counts keyed by event date. The funnel
// AGGREGATES those daily rows over a chosen range (All time / 90 / 30 / 7 days /
// custom) instead of a single day. Each rung is a plain-English stage with a
// click-through detail panel (definition + per-day breakdown + who's behind it),
// and a "Reading the funnel" section underneath narrates the stage-to-stage
// drop-off in non-technical terms. Demo/diag/filled/booked are person-level
// (one human = 1); sent/opened/bounced are email-level.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getCampaignMastersOverview,
  getCampaignMasterDaily,
  CampaignsUnconfigured,
  type CampaignMasterRow,
  type CampaignDailyActivity,
} from '../../api';

// @section 2 Palette + stage definitions
const TONE = {
  top: '#eef1f6', conv: '#14b8a6', good: '#10b981', loss: '#f0606b', watch: '#f59e0b', fill: '#a78bfa',
} as const;

type MetricKey = 'sent' | 'bounced' | 'opens' | 'demo_clicks' | 'diag_visits' | 'filled' | 'booked';
type Hero = 'qty' | 'pct';
type Kind = 'top' | 'conv' | 'loss';

// @block 2.1 Each rung: what it is in plain English + how the % is framed.
type RungDef = {
  key: MetricKey;
  label: string;
  hero: Hero;            // qty rungs show the count big; pct rungs show the rate big
  kind: Kind;
  color: string;
  short: string;         // one-line caption under the number
  long: string;          // non-technical explanation in the detail panel
  denom?: 'sent' | 'delivered' | 'opens' | 'demo_clicks' | 'diag_visits'; // rate denominator
  denomLabel?: string;   // e.g. "of delivered"
  cohort?: 'demo' | 'diag'; // person-stage → link into Leads
};

const RUNG_DEFS: RungDef[] = [
  { key: 'sent', label: 'Attempted', hero: 'qty', kind: 'top', color: TONE.top,
    short: 'emails sent',
    long: 'Every email we sent to a prospect in this period. The top of the funnel — everything downstream is a slice of this.' },
  { key: 'bounced', label: 'Bounced', hero: 'pct', kind: 'loss', color: TONE.loss,
    short: 'of attempted', denom: 'sent', denomLabel: 'of attempted',
    long: 'The email could not be delivered — the address was dead, the mailbox was full, or the receiving server rejected us. A high bounce rate means the list needs cleaning before it hurts our sending reputation.' },
  { key: 'opens', label: 'Opened', hero: 'pct', kind: 'conv', color: TONE.conv,
    short: 'of delivered', denom: 'delivered', denomLabel: 'of delivered',
    long: 'Recipients who opened the email at least once. Measured of DELIVERED mail (not attempted), so bounces do not drag the rate down.' },
  { key: 'demo_clicks', label: 'Demo click', hero: 'qty', kind: 'conv', color: TONE.conv,
    short: 'people viewed the demo', cohort: 'demo',
    long: 'People who clicked through and actually loaded the demo page — a confirmed human visit (analytics filters out bots and inbox link-preview bots). One person counts once.' },
  { key: 'diag_visits', label: 'Diag visited', hero: 'qty', kind: 'conv', color: TONE.good,
    short: 'people opened the diagnostic', cohort: 'diag',
    long: 'People who opened their personalized diagnostic / questionnaire page. The hand-raise step — they are exploring their own numbers.' },
  { key: 'filled', label: 'Filled', hero: 'qty', kind: 'conv', color: TONE.fill,
    short: 'completed the questionnaire',
    long: 'People who filled out and submitted the diagnostic. A strong intent signal. This stage is new and low-volume today; it grows as the diagnostic flow matures.' },
  { key: 'booked', label: 'Booked', hero: 'pct', kind: 'conv', color: TONE.watch,
    short: 'of diag visits', denom: 'diag_visits', denomLabel: 'of diag visits',
    long: 'People who scheduled a call with us — the goal of the whole funnel. Shown as a share of diagnostic visitors (the warmest pool), since most bookings come from people who engaged with their diagnostic.' },
];

// @block 2.2 Helpers
type Totals = Record<MetricKey, number> & { delivered: number };
const EMPTY: Totals = { sent: 0, bounced: 0, opens: 0, demo_clicks: 0, diag_visits: 0, filled: 0, booked: 0, delivered: 0 };

function metric(row: CampaignDailyActivity, k: MetricKey): number {
  if (k === 'filled') return row.filled ?? 0;
  return (row[k] as number) ?? 0;
}
function pctText(p: number | null): string {
  if (p == null) return '—';
  return `${(p * 100).toFixed(p > 0 && p < 0.1 ? 1 : 0)}%`;
}
function fmtDay(d: string): string {
  const dt = new Date(`${d.slice(0, 10)}T00:00:00`);
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

type Preset = 'all' | '7' | '30' | '90' | 'custom';

// @section 3 Main
export default function Funnel() {
  const [masters, setMasters] = useState<CampaignMasterRow[] | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [daily, setDaily] = useState<CampaignDailyActivity[] | null>(null);
  const [masterName, setMasterName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [unconfigured, setUnconfigured] = useState(false);

  // @subsection 3.1 Date range — default to the whole campaign funnel
  const today = new Date().toISOString().slice(0, 10);
  const [preset, setPreset] = useState<Preset>('all');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>(today);
  const [openKey, setOpenKey] = useState<MetricKey | null>(null);

  // @subsection 3.2 Load master list (default biggest by sent)
  useEffect(() => {
    getCampaignMastersOverview()
      .then((d) => {
        setMasters(d.masters);
        if (d.masters.length > 0) setSelectedKey(d.masters[0].master_key);
      })
      .catch((e: unknown) => {
        if (e instanceof CampaignsUnconfigured) setUnconfigured(true); else setErr(String(e));
      });
  }, []);

  // @subsection 3.3 Load daily activity (365d window — covers full campaign history)
  useEffect(() => {
    if (!selectedKey) return;
    setDaily(null); setOpenKey(null);
    setMasterName(masters?.find((m) => m.master_key === selectedKey)?.master_name ?? '');
    getCampaignMasterDaily(selectedKey, 365)
      .then(setDaily)
      .catch((e: unknown) => {
        if (e instanceof CampaignsUnconfigured) setUnconfigured(true); else setErr(String(e));
      });
  }, [selectedKey, masters]);

  // @subsection 3.4 Resolve [from, to] from the preset
  const [from, to] = useMemo<[string, string]>(() => {
    if (preset === 'custom') return [customFrom || '1970-01-01', customTo || today];
    if (preset === 'all') return ['1970-01-01', today];
    const days = Number(preset);
    const f = new Date(Date.now() - (days - 1) * 86_400_000).toISOString().slice(0, 10);
    return [f, today];
  }, [preset, customFrom, customTo, today]);

  // @subsection 3.5 Filter to range + aggregate
  const inRange = useMemo<CampaignDailyActivity[]>(() => {
    if (!daily) return [];
    return daily.filter((d) => {
      const day = d.activity_date.slice(0, 10);
      return day >= from && day <= to;
    });
  }, [daily, from, to]);

  const totals = useMemo<Totals>(() => {
    const t: Totals = { ...EMPTY };
    for (const row of inRange) {
      t.sent += metric(row, 'sent');
      t.bounced += metric(row, 'bounced');
      t.opens += metric(row, 'opens');
      t.demo_clicks += metric(row, 'demo_clicks');
      t.diag_visits += metric(row, 'diag_visits');
      t.filled += metric(row, 'filled');
      t.booked += metric(row, 'booked');
    }
    t.delivered = Math.max(t.sent - t.bounced, 0);
    return t;
  }, [inRange]);

  // @subsection 3.6 Build rungs from the aggregated totals
  const rungs = useMemo(() => {
    const denomVal = (d?: RungDef['denom']) =>
      d === 'delivered' ? totals.delivered : d ? totals[d] : 0;
    return RUNG_DEFS.map((def) => {
      const qty = totals[def.key];
      const pct = def.kind === 'top' ? 1
        : def.denom ? (denomVal(def.denom) > 0 ? qty / denomVal(def.denom) : null)
        : null;
      const survival = totals.sent > 0 ? qty / totals.sent : 0;
      return { def, qty, pct, survival };
    });
  }, [totals]);

  const rangeLabel = preset === 'all' ? 'All time'
    : preset === 'custom' ? `${fmtDay(from)} – ${fmtDay(to)}`
    : `Last ${preset} days`;
  const activeDays = inRange.filter((r) => metric(r, 'sent') > 0 || metric(r, 'opens') > 0).length;
  const hasData = totals.sent > 0 || totals.opens > 0 || totals.demo_clicks > 0;

  return (
    <div className="md:-mx-10 md:-my-8">
      <section className="bg-ink-950 text-paper-100 min-h-screen font-sans">
        <div className="px-4 py-5 md:px-10 md:py-7 max-w-[1600px] mx-auto">

          {/* @subsection 3.7 Breadcrumb + master selector */}
          <div className="flex items-center justify-between text-xs mb-6 flex-wrap gap-3">
            <div className="text-muted">
              <span className="text-paper-100">Atlas</span>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100/80">Campaigns</span>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100/80">Funnel</span>
            </div>
            {masters && masters.length > 0 && (
              <select value={selectedKey ?? ''} onChange={(e) => setSelectedKey(e.target.value)}
                className="bg-ink-900 border border-ink-700 rounded px-3 py-1.5 text-paper-100 text-xs">
                {masters.map((m) => (
                  <option key={m.master_key} value={m.master_key}>
                    {m.master_name} · {m.sent.toLocaleString()} sent
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* @subsection 3.8 Title + range control */}
          <div className="mb-7">
            <div className="text-[11px] uppercase tracking-[3px] text-muted mb-2">
              Pipeline funnel · {rangeLabel.toLowerCase()}
            </div>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-paper-100 mb-4">
              {masterName || (unconfigured ? 'Pipeline not configured' : 'Loading…')}
            </h1>
            <RangeControl
              preset={preset} setPreset={setPreset}
              customFrom={customFrom} setCustomFrom={setCustomFrom}
              customTo={customTo} setCustomTo={setCustomTo} today={today}
            />
          </div>

          {err && <div className="rounded-xl border-2 border-stall/40 bg-ink-900 p-4 mb-6 text-stall text-sm">{err}</div>}
          {unconfigured && (
            <div className="rounded-xl border-2 border-watch/40 bg-ink-900 p-5 mb-6 text-sm text-watch">
              Backend returned 503 — awaiting first refresh.
            </div>
          )}

          {/* @subsection 3.9 The funnel — click a rung to drill in */}
          {daily && hasData && (
            <>
              <div className="flex items-stretch gap-1 overflow-x-auto pb-2">
                {rungs.map((r, i) => (
                  <div key={r.def.key} className="flex items-stretch">
                    <RungCard r={r} open={openKey === r.def.key}
                      onClick={() => setOpenKey(openKey === r.def.key ? null : r.def.key)} />
                    {i < rungs.length - 1 && <Connector />}
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-muted mb-6">{activeDays} active day(s) in range · click any tile for detail</div>

              {/* @subsection 3.10 Drill-in detail for the selected rung */}
              {openKey && (
                <RungDetail
                  def={RUNG_DEFS.find((d) => d.key === openKey)!}
                  totals={totals} inRange={inRange}
                  onClose={() => setOpenKey(null)}
                />
              )}

              {/* @subsection 3.11 Reading the funnel — non-technical drop-off narrative */}
              <FunnelReadout totals={totals} />
            </>
          )}

          {daily && !hasData && (
            <div className="text-muted text-sm py-20 text-center">
              No activity in {rangeLabel.toLowerCase()} for this campaign. Widen the range.
            </div>
          )}
          {!daily && !err && !unconfigured && (
            <div className="text-muted text-sm py-20 text-center">Loading funnel…</div>
          )}

        </div>
      </section>
    </div>
  );
}

// @section 4 Presentational

// @block 4.1 Date-range control — preset chips + custom from/to
function RangeControl({ preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, today }: {
  preset: Preset; setPreset: (p: Preset) => void;
  customFrom: string; setCustomFrom: (v: string) => void;
  customTo: string; setCustomTo: (v: string) => void; today: string;
}) {
  const chips: { k: Preset; label: string }[] = [
    { k: 'all', label: 'All time' }, { k: '90', label: '90 days' },
    { k: '30', label: '30 days' }, { k: '7', label: '7 days' }, { k: 'custom', label: 'Custom' },
  ];
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs">
      <div className="flex items-center gap-1 rounded-lg bg-ink-900 border border-ink-800 p-1">
        {chips.map((c) => (
          <button key={c.k} onClick={() => setPreset(c.k)}
            className={`px-3 py-1 rounded-md transition-colors ${
              preset === c.k ? 'bg-accent-500 text-ink-950 font-semibold' : 'text-muted hover:text-paper-100'
            }`}>
            {c.label}
          </button>
        ))}
      </div>
      {preset === 'custom' && (
        <div className="flex items-center gap-2 text-muted">
          <input type="date" value={customFrom} max={customTo || today}
            onChange={(e) => setCustomFrom(e.target.value)}
            className="bg-ink-900 border border-ink-700 rounded px-2 py-1 text-paper-100" />
          <span>→</span>
          <input type="date" value={customTo} max={today}
            onChange={(e) => setCustomTo(e.target.value)}
            className="bg-ink-900 border border-ink-700 rounded px-2 py-1 text-paper-100" />
        </div>
      )}
    </div>
  );
}

type RungVM = { def: RungDef; qty: number; pct: number | null; survival: number };

// @block 4.2 Rung card (clickable)
function RungCard({ r, open, onClick }: { r: RungVM; open: boolean; onClick: () => void }) {
  const { def, qty, pct, survival } = r;
  const heroText = def.hero === 'qty' ? qty.toLocaleString() : (def.kind === 'top' ? '100%' : pctText(pct));
  const secondary = def.hero === 'qty' ? null : qty.toLocaleString();
  return (
    <button onClick={onClick}
      className={`flex flex-col text-left min-w-[160px] rounded-xl border bg-ink-900/60 p-5 transition-all hover:bg-ink-900 ${
        open ? 'border-accent-500 ring-1 ring-accent-500/40' : 'border-ink-800'
      }`}>
      <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-3">{def.label}</div>
      <div className="text-6xl font-bold tabular-nums leading-none" style={{ color: def.color }}>{heroText}</div>
      <div className="text-[11px] text-muted mt-1">{def.short}</div>
      {secondary && (
        <div className="text-2xl font-semibold tabular-nums text-paper-100/90 mt-4">{secondary}</div>
      )}
      <div className="mt-auto pt-4">
        <div className="h-2 rounded-full bg-ink-800 overflow-hidden">
          <div className="h-full rounded-full transition-all"
            style={{ width: `${Math.max(survival * 100, qty > 0 ? 2 : 0)}%`, backgroundColor: def.color }} />
        </div>
        <div className="text-[10px] text-muted mt-1 tabular-nums">
          {(survival * 100).toFixed(survival > 0 && survival < 0.1 ? 1 : 0)}% of attempted
        </div>
      </div>
    </button>
  );
}

// @block 4.3 Connector chevron
function Connector() {
  return (
    <div className="flex items-center px-1 text-ink-700">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

// @block 4.4 Drill-in detail — definition + per-day breakdown + who's behind it
function RungDetail({ def, totals, inRange, onClose }: {
  def: RungDef; totals: Totals; inRange: CampaignDailyActivity[]; onClose: () => void;
}) {
  const series = useMemo(() => {
    return inRange
      .map((row) => ({ date: row.activity_date.slice(0, 10), value: metric(row, def.key) }))
      .filter((d) => d.value > 0)
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [inRange, def.key]);
  const max = Math.max(1, ...series.map((s) => s.value));
  const denomVal = def.denom === 'delivered' ? totals.delivered : def.denom ? totals[def.denom] : 0;
  const rate = def.denom && denomVal > 0 ? totals[def.key] / denomVal : null;

  return (
    <div className="rounded-xl border border-accent-500/40 bg-ink-900/60 p-6 mb-8">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[2px] text-accent-400 font-semibold mb-1">
            {def.label} · {totals[def.key].toLocaleString()}
            {rate != null && <span className="text-muted"> · {pctText(rate)} {def.denomLabel}</span>}
          </div>
          <p className="text-sm text-paper-100/80 max-w-3xl leading-relaxed">{def.long}</p>
        </div>
        <button onClick={onClose} className="text-muted hover:text-paper-100 text-xs ml-4">✕ close</button>
      </div>

      {def.cohort && (
        <Link to="/campaigns/leads"
          className="inline-block text-xs text-accent-400 hover:text-accent-300 mb-4">
          → See these people in Leads
        </Link>
      )}

      <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-2 mt-2">By day</div>
      {series.length === 0 ? (
        <div className="text-xs text-muted">No days with activity for this stage in range.</div>
      ) : (
        <div className="space-y-1 max-h-60 overflow-auto pr-2">
          {series.map((s) => (
            <div key={s.date} className="flex items-center gap-3 text-xs">
              <span className="text-muted w-16 tabular-nums shrink-0">{fmtDay(s.date)}</span>
              <div className="flex-1 h-3 rounded bg-ink-800 overflow-hidden">
                <div className="h-full rounded" style={{ width: `${(s.value / max) * 100}%`, backgroundColor: def.color }} />
              </div>
              <span className="text-paper-100/90 tabular-nums w-12 text-right shrink-0">{s.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// @block 4.5 Reading the funnel — plain-English stage-to-stage drop-off
function FunnelReadout({ totals }: { totals: Totals }) {
  const pct = (n: number, d: number) => (d > 0 ? n / d : null);
  const steps = [
    { label: 'Delivered', value: totals.delivered, of: totals.sent,
      text: `Of ${totals.sent.toLocaleString()} sent, ${totals.bounced.toLocaleString()} bounced (${pctText(pct(totals.bounced, totals.sent))}). ${totals.delivered.toLocaleString()} reached an inbox.` },
    { label: 'Opened', value: totals.opens, of: totals.delivered,
      text: `${totals.opens.toLocaleString()} of ${totals.delivered.toLocaleString()} delivered emails were opened — a ${pctText(pct(totals.opens, totals.delivered))} open rate.` },
    { label: 'Demo viewed', value: totals.demo_clicks, of: totals.opens,
      text: `${totals.demo_clicks.toLocaleString()} people clicked through and viewed the demo — ${pctText(pct(totals.demo_clicks, totals.opens))} of openers.` },
    { label: 'Diagnostic opened', value: totals.diag_visits, of: totals.demo_clicks,
      text: `${totals.diag_visits.toLocaleString()} opened their diagnostic page${totals.demo_clicks > 0 ? ` (${pctText(pct(totals.diag_visits, totals.demo_clicks))} of demo viewers)` : ''}.` },
    { label: 'Questionnaire filled', value: totals.filled, of: totals.diag_visits,
      text: `${totals.filled.toLocaleString()} completed the questionnaire${totals.diag_visits > 0 ? ` (${pctText(pct(totals.filled, totals.diag_visits))} of diagnostic visitors)` : ''}.` },
    { label: 'Booked', value: totals.booked, of: totals.diag_visits,
      text: `${totals.booked.toLocaleString()} booked a meeting${totals.diag_visits > 0 ? ` (${pctText(pct(totals.booked, totals.diag_visits))} of diagnostic visitors)` : ''} — the goal of the funnel.` },
  ];
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/40 p-6">
      <div className="text-[11px] uppercase tracking-[3px] text-muted mb-1">Reading the funnel</div>
      <p className="text-xs text-muted mb-5 max-w-3xl">
        Each step is a share of the step before it — that is where people drop off. Email-level stages
        (sent, delivered, opened) count emails; demo, diagnostic, filled and booked count distinct people.
      </p>
      <div className="space-y-3">
        {steps.map((s) => (
          <div key={s.label} className="flex items-start gap-4">
            <div className="w-40 shrink-0">
              <div className="text-sm font-semibold text-paper-100">{s.label}</div>
              <div className="text-[11px] text-muted tabular-nums">
                {s.value.toLocaleString()}{s.of > 0 && <> · {pctText(pct(s.value, s.of))} of prior</>}
              </div>
            </div>
            <p className="text-sm text-paper-100/75 leading-relaxed flex-1">{s.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
